/**
 * Paper-photo pipeline (pure math, no DOM): 4-corner perspective straighten, auto levels /
 * contrast, journal filters, and ink-mask extraction. The client feeds it ImageData-like pixels.
 */

export interface Img {
  width: number;
  height: number;
  /** RGBA, row-major (ImageData-compatible). */
  data: Uint8ClampedArray<ArrayBuffer>;
}

export interface Mask {
  width: number;
  height: number;
  /** 1 = ink, 0 = paper. */
  data: Uint8Array;
}

export interface P2 {
  x: number;
  y: number;
}
export interface Quad {
  tl: P2;
  tr: P2;
  br: P2;
  bl: P2;
}

export const makeImg = (width: number, height: number): Img => ({ width, height, data: new Uint8ClampedArray(width * height * 4) });
export const makeMask = (width: number, height: number): Mask => ({ width, height, data: new Uint8Array(width * height) });

/* -------------------------------- Homography -------------------------------- */

/** Solve A x = b (n x n) with partial-pivot Gaussian elimination. */
function solve(A: number[][], b: number[]): number[] {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]!]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r]![c]!) > Math.abs(M[p]![c]!)) p = r;
    [M[c], M[p]] = [M[p]!, M[c]!];
    const piv = M[c]![c]!;
    if (Math.abs(piv) < 1e-12) throw new Error('singular');
    for (let k = c; k <= n; k++) M[c]![k]! /= piv;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r]![c]!;
      if (!f) continue;
      for (let k = c; k <= n; k++) M[r]![k]! -= f * M[c]![k]!;
    }
  }
  return M.map((row) => row[n]!);
}

/**
 * 3x3 homography (row-major, h33 = 1) mapping points `from[i]` → `to[i]` (4 correspondences).
 */
export function homography(from: P2[], to: P2[]): number[] {
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = from[i]!;
    const { x: u, y: v } = to[i]!;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }
  return [...solve(A, b), 1];
}

export function applyH(H: number[], p: P2): P2 {
  const w = H[6]! * p.x + H[7]! * p.y + H[8]!;
  return { x: (H[0]! * p.x + H[1]! * p.y + H[2]!) / w, y: (H[3]! * p.x + H[4]! * p.y + H[5]!) / w };
}

/** Straighten the quad region of `src` into a `w` x `h` rectangle (bilinear sampling). */
export function warpQuad(src: Img, quad: Quad, w: number, h: number): Img {
  const dst = makeImg(w, h);
  // Inverse mapping: destination rect corners → source quad corners.
  const H = homography(
    [
      { x: 0, y: 0 },
      { x: w - 1, y: 0 },
      { x: w - 1, y: h - 1 },
      { x: 0, y: h - 1 },
    ],
    [quad.tl, quad.tr, quad.br, quad.bl],
  );
  const sw = src.width;
  const sh = src.height;
  const s = src.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = applyH(H, { x, y });
      const fx = Math.max(0, Math.min(sw - 1.001, p.x));
      const fy = Math.max(0, Math.min(sh - 1.001, p.y));
      const x0 = Math.floor(fx);
      const y0 = Math.floor(fy);
      const ax = fx - x0;
      const ay = fy - y0;
      const i00 = (y0 * sw + x0) * 4;
      const i10 = i00 + 4;
      const i01 = i00 + sw * 4;
      const i11 = i01 + 4;
      const o = (y * w + x) * 4;
      for (let c = 0; c < 4; c++) {
        const top = s[i00 + c]! * (1 - ax) + s[i10 + c]! * ax;
        const bot = s[i01 + c]! * (1 - ax) + s[i11 + c]! * ax;
        dst.data[o + c] = top * (1 - ay) + bot * ay;
      }
    }
  }
  return dst;
}

/** Default crop corners: inset 6% from each edge. */
export function defaultQuad(w: number, h: number, inset = 0.06): Quad {
  const dx = w * inset;
  const dy = h * inset;
  return { tl: { x: dx, y: dy }, tr: { x: w - dx, y: dy }, br: { x: w - dx, y: h - dy }, bl: { x: dx, y: h - dy } };
}

/** Corner nearest to a point (drag handle hit-test). */
export function closestCorner(q: Quad, p: P2): keyof Quad {
  let best: keyof Quad = 'tl';
  let bd = Infinity;
  for (const k of ['tl', 'tr', 'br', 'bl'] as const) {
    const d = Math.hypot(q[k].x - p.x, q[k].y - p.y);
    if (d < bd) {
      bd = d;
      best = k;
    }
  }
  return best;
}

/** Output size keeping the quad's average aspect ratio, with the long side = `long`. */
export function quadOutputSize(q: Quad, long = 800): { w: number; h: number } {
  const top = Math.hypot(q.tr.x - q.tl.x, q.tr.y - q.tl.y);
  const bottom = Math.hypot(q.br.x - q.bl.x, q.br.y - q.bl.y);
  const left = Math.hypot(q.bl.x - q.tl.x, q.bl.y - q.tl.y);
  const right = Math.hypot(q.br.x - q.tr.x, q.br.y - q.tr.y);
  const aw = (top + bottom) / 2;
  const ah = (left + right) / 2;
  if (aw >= ah) return { w: long, h: Math.max(1, Math.round((long * ah) / Math.max(1, aw))) };
  return { w: Math.max(1, Math.round((long * aw) / Math.max(1, ah))), h: long };
}

/* ---------------------------- Tone / colour correction ---------------------------- */

export function luminance(img: Img): Uint8Array {
  const out = new Uint8Array(img.width * img.height);
  const d = img.data;
  for (let i = 0, j = 0; j < out.length; i += 4, j++) out[j] = (d[i]! * 299 + d[i + 1]! * 587 + d[i + 2]! * 114) / 1000;
  return out;
}

function percentile(hist: number[], total: number, q: number): number {
  let acc = 0;
  for (let i = 0; i < 256; i++) {
    acc += hist[i]!;
    if (acc >= total * q) return i;
  }
  return 255;
}

/** Stretch levels so the 2nd percentile becomes black and the 98th white (paper looks white). */
export function autoLevels(img: Img, lo = 0.02, hi = 0.98): Img {
  const g = luminance(img);
  const hist = new Array(256).fill(0);
  for (const v of g) hist[v]++;
  const a = percentile(hist, g.length, lo);
  const b = Math.max(a + 1, percentile(hist, g.length, hi));
  const out = makeImg(img.width, img.height);
  const scale = 255 / (b - a);
  for (let i = 0; i < img.data.length; i += 4) {
    for (let c = 0; c < 3; c++) out.data[i + c] = (img.data[i + c]! - a) * scale;
    out.data[i + 3] = img.data[i + 3]!;
  }
  return out;
}

/** Contrast around mid-grey; amount 1 = unchanged. */
export function contrast(img: Img, amount: number): Img {
  const out = makeImg(img.width, img.height);
  for (let i = 0; i < img.data.length; i += 4) {
    for (let c = 0; c < 3; c++) out.data[i + c] = (img.data[i + c]! - 128) * amount + 128;
    out.data[i + 3] = img.data[i + 3]!;
  }
  return out;
}

export type JournalFilter = 'none' | 'clean' | 'warm' | 'cool' | 'mono' | 'pop';
export const JOURNAL_FILTERS: JournalFilter[] = ['none', 'clean', 'warm', 'cool', 'mono', 'pop'];

/** Simple per-channel "LUT" filters for journal photos. */
export function applyFilter(img: Img, f: JournalFilter): Img {
  if (f === 'none') return img;
  if (f === 'clean') return contrast(autoLevels(img), 1.15);
  const out = makeImg(img.width, img.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i]!;
    const g = d[i + 1]!;
    const b = d[i + 2]!;
    const y = (r * 299 + g * 587 + b * 114) / 1000;
    let o: [number, number, number];
    switch (f) {
      case 'warm':
        o = [r * 1.08 + 10, g * 1.02 + 4, b * 0.9];
        break;
      case 'cool':
        o = [r * 0.92, g * 1.0 + 2, b * 1.08 + 10];
        break;
      case 'mono':
        o = [y, y, y];
        break;
      case 'pop':
        o = [(r - 128) * 1.3 + 128 + (r - y) * 0.4, (g - 128) * 1.3 + 128 + (g - y) * 0.4, (b - 128) * 1.3 + 128 + (b - y) * 0.4];
        break;
    }
    out.data[i] = o[0];
    out.data[i + 1] = o[1];
    out.data[i + 2] = o[2];
    out.data[i + 3] = d[i + 3]!;
  }
  return out;
}

/* ---------------------------------- Ink mask ---------------------------------- */

/** Otsu threshold of a grey histogram. */
export function otsu(gray: Uint8Array): number {
  const hist = new Array(256).fill(0);
  for (const v of gray) hist[v]++;
  const total = gray.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i]!;
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let thr = 127;
  for (let i = 0; i < 256; i++) {
    wB += hist[i]!;
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += i * hist[i]!;
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > best) {
      best = between;
      thr = i;
    }
  }
  return thr;
}

/**
 * Ink mask robust to uneven lighting: each pixel is compared with the local paper brightness
 * (bright percentile of its block). Pixels clearly darker than the paper are ink.
 */
export function inkMask(img: Img, opts: { block?: number; darkness?: number } = {}): Mask {
  const { width: w, height: h } = img;
  const g = luminance(img);
  const block = opts.block ?? Math.max(8, Math.round(Math.min(w, h) / 12));
  const darkness = opts.darkness ?? 0.72;
  const bw = Math.ceil(w / block);
  const bh = Math.ceil(h / block);
  const bg = new Float32Array(bw * bh);
  for (let by = 0; by < bh; by++) {
    for (let bx = 0; bx < bw; bx++) {
      const vals: number[] = [];
      for (let y = by * block; y < Math.min(h, (by + 1) * block); y += 2) for (let x = bx * block; x < Math.min(w, (bx + 1) * block); x += 2) vals.push(g[y * w + x]!);
      vals.sort((a, b) => a - b);
      bg[by * bw + bx] = vals[Math.floor(vals.length * 0.9)] ?? 255;
    }
  }
  const m = makeMask(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const paper = bg[Math.floor(y / block) * bw + Math.floor(x / block)]!;
      // Very dark paper (no light / blank black photo) → nothing counts as ink.
      if (paper < 60) continue;
      if (g[y * w + x]! < paper * darkness) m.data[y * w + x] = 1;
    }
  }
  return m;
}

/** Nearest-neighbour resize of a mask (OR-pooling when shrinking so thin lines survive). */
export function resizeMask(m: Mask, w: number, h: number): Mask {
  const out = makeMask(w, h);
  const sx = m.width / w;
  const sy = m.height / h;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = 0;
      const x0 = Math.floor(x * sx);
      const y0 = Math.floor(y * sy);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      const y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
      for (let yy = y0; yy < y1 && !v; yy++) for (let xx = x0; xx < x1 && !v; xx++) v = m.data[yy * m.width + xx] ?? 0;
      out.data[y * w + x] = v;
    }
  }
  return out;
}

export function dilate(m: Mask, r: number): Mask {
  if (r <= 0) return m;
  const { width: w, height: h } = m;
  const tmp = makeMask(w, h);
  const out = makeMask(w, h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let v = 0;
      for (let k = -r; k <= r && !v; k++) v = m.data[y * w + Math.min(w - 1, Math.max(0, x + k))]!;
      tmp.data[y * w + x] = v;
    }
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let v = 0;
      for (let k = -r; k <= r && !v; k++) v = tmp.data[Math.min(h - 1, Math.max(0, y + k)) * w + x]!;
      out.data[y * w + x] = v;
    }
  return out;
}

/** Remove isolated specks (pixels with fewer than `min` ink neighbours in 3x3). */
export function despeckle(m: Mask, min = 2): Mask {
  const { width: w, height: h } = m;
  const out = makeMask(w, h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      if (!m.data[y * w + x]) continue;
      let n = 0;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          const xx = x + dx;
          const yy = y + dy;
          if (xx >= 0 && yy >= 0 && xx < w && yy < h) n += m.data[yy * w + xx]!;
        }
      if (n >= min) out.data[y * w + x] = 1;
    }
  return out;
}

export const inkCount = (m: Mask) => m.data.reduce((n, v) => n + v, 0);
